import { useCallback, useEffect, useRef, useState } from 'react';
import { GLOBAL_DEBOUNCE_TIME } from '../../utils/DataConstants';

const SAVE_FAILED = 'Not saved. Your last change is still here but the service has not taken it.';

/**
 * When the service is told about a plan the author has already changed.
 *
 * A pause and a send, which is what the document editor in
 * `LocationDocuments` does and all this needs to be. The plan is in the store —
 * the canvas writes it there and draws from there — so there is no copy here to
 * go stale, and each save carries the whole current plan, which means a later
 * request holds everything an earlier one did.
 *
 * It used to order the writes and let a caller wait for them, through a promise
 * chain and three more pieces of state. Both existed for publish and restore,
 * which must not act on a document missing the author's last edit. Those
 * controls are held while a save is outstanding instead — the screen says so
 * and they come back in under a second — so by the time either runs there is
 * nothing to wait for, and the ordering it was protecting is no longer
 * load-bearing.
 */
export const useDraftSave = <TPlan>(send: (plan: TPlan) => Promise<any>) => {
	/*
	  The current `send`, so a write started by a timer or by unmounting uses
	  this render's dispatch rather than the one captured when the timer was set.
	*/
	const sendRef = useRef(send);
	sendRef.current = send;

	const timer = useRef<any>(null);
	/** The newest plan, while it is still waiting out the pause. */
	const waiting = useRef<TPlan | null>(null);
	/** Whether a write is out. One at a time; see `run`. */
	const inFlight = useRef(false);
	/** The write that is out, for the one caller that cannot wait its turn. */
	const sent = useRef<Promise<any>>(Promise.resolve());

	const [isSaving, setIsSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);

	/*
	  One write at a time, and the newest plan goes next.

	  Not for tidiness: the draft is written whole, so two requests in the air
	  have no order and the service keeps whichever it finishes last. Letting the
	  older one win is not just a lost edit — once both answer, `isSaving` clears
	  and Publish comes back, and the version it then cuts is the older plan while
	  the canvas shows the newer. Holding Publish while a save is outstanding does
	  not help with that, because by then nothing is outstanding.

	  So an edit arriving while a write is out waits for it, in `waiting`, where a
	  newer one replaces it. That is cheaper than ordering the requests and says
	  the same thing: the service is only ever told the newest plan, once.
	*/
	const runRef = useRef<(plan: TPlan) => void>(() => undefined);

	runRef.current = (plan: TPlan) => {
		if (inFlight.current) {
			waiting.current = plan;
			return;
		}

		inFlight.current = true;
		setIsSaving(true);

		sent.current = sendRef.current(plan);
		sent.current
			.then(
				() => setError(null),
				// Kept on screen: the plan is in the store and the service has not
				// taken it, which is the one case where silence would lie. It also
				// holds Publish, because a version cut now would be missing it.
				() => setError(SAVE_FAILED)
			)
			.then(() => {
				inFlight.current = false;

				/*
				  Only a plan that has already waited out its pause.

				  `push` puts the newest plan here straight away, so taking whatever
				  is here would send an edit the moment the last request answered —
				  turning a burst of edits into one whole-draft write per round trip
				  and undoing the pause for exactly the stretch it is wanted most. A
				  plan still inside its pause belongs to the timer, which is running
				  and will call back.
				*/
				const next = timer.current === null ? waiting.current : null;

				if (next !== null) {
					waiting.current = null;
					runRef.current(next);
					return;
				}

				// Still saving if a pause is running: that edit is on its way.
				if (timer.current === null) {
					setIsSaving(false);
				}
			});
	};

	/** An edit. Starts or restarts the pause. */
	const push = useCallback((plan: TPlan) => {
		waiting.current = plan;
		setIsSaving(true);

		if (timer.current) {
			clearTimeout(timer.current);
		}

		timer.current = setTimeout(() => {
			timer.current = null;
			const held = waiting.current;
			waiting.current = null;

			if (held !== null) {
				runRef.current(held);
			}
		}, GLOBAL_DEBOUNCE_TIME);
	}, []);

	/*
	  An edit still waiting when the tab closes is sent rather than dropped — but
	  behind whatever is already out, not beside it.
	
	  This is the one place that cannot wait its turn the way `run` does, because
	  the screen is gone and nothing is left to call it back. So it waits on the
	  request itself. Sending straight out looked harmless and was not: with a
	  write already in the air the two had no order, and the service keeping the
	  older of them loses the edit this is here to save.

	  The requests outlive this screen; the state they would set does not, and
	  React discards that.
	*/
	useEffect(
		() => () => {
			if (timer.current) {
				clearTimeout(timer.current);
				timer.current = null;
			}

			const held = waiting.current;
			waiting.current = null;

			if (held !== null) {
				sent.current.then(
					() => sendRef.current(held),
					() => sendRef.current(held)
				);
			}
		},
		[]
	);

	return { push, isSaving, error };
};
